import { useEffect, useRef, type ReactNode } from 'react';
import { followsDoubleTap, isDoubleTap } from '@/shared/device/doubleTap';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Icon } from '@/shared/components/ui/Icon';
import { formatRelativeTime } from '@/shared/i18n';
import { useI18n } from '@/shared/i18n/useT';
import { editorPathFor, folderPath, templateFor } from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';
import { NewBadge } from './NewBadge';
import { NodeMenu, type NodeAction } from './NodeMenu';

interface Props {
  node: FoldersNode;
  actions: readonly NodeAction[];
  /** Paints the star badge on the preview. Omit outside the sections that care. */
  starred?: boolean;
  onToggleStar?: () => void;
  /** Owner initials in the corner — the "someone else made this" signal. */
  ownerName?: string | null;
  /** An accent dot before the name: shared with you and not opened yet. */
  unread?: boolean;
  /** Replaces the default "Sheet · Edited …" line. */
  meta?: ReactNode;
}

/**
 * One folder or one file, as a card.
 *
 * Canva rather than Folders: a large preview area on top and the name underneath,
 * not a dense row in a table. Folders are deliberately shorter — they hold
 * things, they do not preview as anything — which also makes the two groups
 * distinguishable at a glance without a second visual language.
 *
 * Home reuses this for Starred, Frequently opened and Shared. The
 * extra props are all optional and all default to the browser's behaviour, so
 * there is one card and not four that drift.
 */
export function NodeCard({
  node,
  actions,
  starred,
  onToggleStar,
  ownerName,
  unread,
  meta,
}: Props) {
  const { t, formatLocale } = useI18n();
  const navigate = useNavigate();
  const clickTimer = useRef<number | null>(null);

  const isFolder = node.kind === 'folder';
  const template = templateFor(node.docType);
  const to = isFolder ? folderPath(node.id) : editorPathFor(node.id, node.docType);
  const renameAction = actions.find((action) => action.id === 'rename');

  useEffect(() => () => {
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
  }, []);

  return (
    <Link
      to={to}
      onClick={(event) => {
        if (!renameAction || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (clickTimer.current !== null) {
          window.clearTimeout(clickTimer.current);
          clickTimer.current = null;
          return;
        }
        clickTimer.current = window.setTimeout(() => {
          clickTimer.current = null;
          navigate(to);
        }, 250);
      }}
      onPointerUp={(event) => {
        if (renameAction && isDoubleTap(event)) renameAction.run();
      }}
      onDoubleClick={(event) => {
        if (!renameAction || followsDoubleTap(event)) return;
        event.preventDefault();
        event.stopPropagation();
        if (clickTimer.current !== null) {
          window.clearTimeout(clickTimer.current);
          clickTimer.current = null;
        }
        renameAction.run();
      }}
      className="group flex flex-col rounded-token border border-border bg-surface-2 transition-colors hover:border-border-strong"
    >
      <div
        className={[
          'relative flex items-center justify-center overflow-hidden rounded-t-token bg-surface-3 text-muted',
          isFolder ? 'h-16' : 'h-24 sm:h-28',
        ].join(' ')}
      >
        <Icon
          name={isFolder ? 'folder' : (template?.icon ?? 'sheets')}
          className={isFolder ? 'h-7 w-7' : 'h-9 w-9'}
        />

        {onToggleStar && (
          <button
            type="button"
            aria-label={t(starred ? 'folders.unstar' : 'folders.star')}
            aria-pressed={starred ?? false}
            onClick={(event) => {
              // The card is a link; the star must not navigate.
              event.preventDefault();
              event.stopPropagation();
              onToggleStar();
            }}
            className={[
              'absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-full',
              starred ? 'text-accent' : 'text-muted opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
            ].join(' ')}
          >
            <Icon name="star" className="h-4 w-4" filled={starred ?? false} />
          </button>
        )}

        {ownerName !== undefined && !onToggleStar && (
          <span className="absolute right-2 top-2">
            <Avatar name={ownerName} label={null} />
          </span>
        )}
      </div>

      <div className="flex items-start gap-1 p-2 sm:p-3">
        <div className="min-w-0 flex-1">
          <p className="flex min-h-5 items-center gap-1.5 break-words text-sm font-medium text-fg">
            <span className="min-w-0 break-words">{node.name}</span>
            {unread && <NewBadge />}
          </p>
          <p className="min-h-8 break-words text-xs text-muted">
            {meta ?? (
              <>
                {!isFolder && template && <>{t(template.nameKey)} · </>}
                {t('folders.editedOn', {
                  date: formatRelativeTime(node.updatedAt, formatLocale),
                })}
              </>
            )}
          </p>
        </div>
        {actions.length > 0 && <NodeMenu nodeName={node.name} actions={actions} />}
      </div>
    </Link>
  );
}
